import { Check } from "lucide-react";
import {
  CollabPhase,
  PHASE_LABELS,
  PHASE_ORDER,
} from "../../../models/audit/collabPhase";

interface CollabPhaseIndicatorProps {
  currentPhase: CollabPhase;
  isStreaming?: boolean;
  /**
   * The steps to draw, in order. Defaults to the shared collab phase order;
   * no caller passes this today.
   */
  phaseOrder?: CollabPhase[];
  /**
   * Step captions, merged over the shared defaults, so an entity that wants to
   * rename one step doesn't have to restate the other five.
   */
  phaseLabels?: Partial<Record<CollabPhase, string>>;
}

/**
 * The stepper across the top of a collab wizard.
 *
 * Entity-agnostic by default: the phase order and the step labels come from the
 * shared phase vocabulary. Both are overridable so that stops being an
 * assumption baked into this file — the entity-specific *descriptions* live in
 * each model's `PHASE_DISPLAY`, which the page renders, not this component.
 */
export const CollabPhaseIndicator = ({
  currentPhase,
  isStreaming = false,
  phaseOrder = PHASE_ORDER,
  phaseLabels,
}: CollabPhaseIndicatorProps) => {
  const labels = phaseLabels
    ? { ...PHASE_LABELS, ...phaseLabels }
    : PHASE_LABELS;
  const currentIndex = phaseOrder.indexOf(currentPhase);

  // "complete" isn't drawn as its own step - reaching it turns every step green.
  const displayPhases = phaseOrder.filter((phase) => phase !== "complete");

  return (
    <div className="flex items-center justify-between w-full max-w-2xl mx-auto px-4">
      {displayPhases.map((phase, index) => {
        const isCompleted =
          currentPhase === "complete" ||
          (currentIndex > index && currentPhase !== "failed");
        const isCurrent = phase === currentPhase;

        return (
          <div key={phase} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  transition-colors duration-200
                  ${
                    isCompleted
                      ? "bg-green-500 text-white"
                      : isCurrent
                        ? `bg-stone-800 text-white${isStreaming ? " animate-pulse" : ""}`
                        : "bg-stone-200 text-stone-500"
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span
                className={`
                  text-xs mt-1 whitespace-nowrap
                  ${isCurrent || isCompleted ? "text-stone-800 font-medium" : "text-stone-500"}
                `}
              >
                {labels[phase]}
              </span>
            </div>
            {index < displayPhases.length - 1 && (
              <div
                className={`
                  h-0.5 flex-1 mx-2 transition-colors duration-200
                  ${isCompleted ? "bg-green-500" : "bg-stone-200"}
                `}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
